import type { ClaudeModel, EffortLevel, Lane, SpecContent } from "@szyos/contracts";
import { CLAUDE_MODEL_IDS, EFFORT_LEVELS } from "@szyos/contracts";

/**
 * Die Beraterin: schlägt je Aufgabe Modell und Aufwand vor. Grundsatz des Owners: so hoch wie nötig,
 * so sparsam wie möglich (23.10.2026: Effizienz ernst nehmen, das Abo teilen sich mehrere Personen).
 * Regelbasiert und begründet, damit jeder Vorschlag nachvollziehbar und testbar bleibt.
 */

/** Anzeige und grober Verbrauch je Modell; Opus ist die Bezugsgröße (1). Stand: Modellübersicht 10/2026. */
export const MODEL_INFO: Record<
  ClaudeModel,
  { label: string; cliModel: string; relativeCost: number; use: string }
> = {
  fable: {
    label: "Fable 5.1",
    cliModel: CLAUDE_MODEL_IDS.fable,
    relativeCost: 2.5,
    use: "leistungsfähigstes Modell für die schwierigsten, langen Aufgaben",
  },
  opus: {
    label: "Opus 5.5",
    cliModel: CLAUDE_MODEL_IDS.opus,
    relativeCost: 1,
    use: "Standard für anspruchsvolles Programmieren und Planen",
  },
  sonnet: {
    label: "Sonnet 5.5",
    cliModel: CLAUDE_MODEL_IDS.sonnet,
    relativeCost: 0.5,
    use: "schnell und sparsam für Doku und Alltägliches",
  },
};

/**
 * Zeitlimit eines Arbeitslaufs je Aufwand. Gründliche Läufe denken lange; ein zu knappes Limit bricht sie kurz vor
 * dem Ende ab und verbrennt Kontingent (22.10.2026: zwei Läufe mit „max“ nach 600 s beendet).
 */
export const RUN_TIMEOUT_SECONDS: Record<EffortLevel, number> = {
  low: 900,
  medium: 1800,
  high: 2700,
  xhigh: 3600,
  max: 3600,
};

export function runTimeoutSeconds(effort: EffortLevel | null | undefined): number {
  return RUN_TIMEOUT_SECONDS[effort ?? "high"];
}

export const EFFORT_INFO: Record<EffortLevel, string> = {
  low: "niedrig: knapp, für einfache Handgriffe",
  medium: "mittel: solide für Routine",
  high: "hoch: gründlich, Mindestmaß für Anspruchsvolles",
  xhigh: "sehr hoch: beste Stufe für Programmieren und Agentenarbeit",
  max: "maximal: wenn Richtigkeit wichtiger ist als Verbrauch",
};

export interface AdviceInput {
  lane: Lane;
  title: string;
  acceptanceCriteria: readonly string[];
  spec?: Pick<SpecContent, "problem" | "mustHaves" | "nonGoals" | "designNotes" | "acceptance"> | null;
  platform?: string;
}

export interface Advice {
  model: ClaudeModel;
  effort: EffortLevel;
  reasons: string[];
  /** Höherer Aufwand, den die Beraterin empfiehlt, aber nicht selbst einstellt; `null`, wenn keiner nötig ist. */
  recommendedEffort: EffortLevel | null;
}

/**
 * Hinweise auf heikle oder schwierige Arbeit; ein Treffer hebt den Aufwand an. Kurze Wörter zählen nur am
 * Wortanfang (`(?<![\p{L}-])`): Sonst wurde „Scrollen“ zur Rolle, „async“ zur Synchronisierung und
 * „Design-Tokens“ zur Anmeldung (24.10.2026: eine reine CSS-Aufgabe galt als heikel). „Rechte“ fehlt bewusst,
 * weil es im Layout meist die rechte Seite meint.
 */
const RISK_SIGNALS: { re: RegExp; reason: string }[] = [
  {
    re: /sicherheit|security|passwort|(?<![\p{L}-])login|anmeld|passkey|(?<![\p{L}-])token|verschlüssel|berechtigung|zugriffsrecht|(?<![\p{L}-])rolle/iu,
    reason: "Sicherheit oder Anmeldung",
  },
  { re: /zahlung|bezahl|payment|(?<![\p{L}-])abo(?!\p{L})|rechnung|stripe/iu, reason: "Zahlungen" },
  {
    re: /datenbank|migration|synchron|(?<![\p{L}-])sync|offline|speicher|backup|datenverlust/iu,
    reason: "Daten, Speicherung oder Synchronisierung",
  },
  {
    re: /datenschutz|dsgvo|personenbezogen|(?<![\p{L}-])kinder|schüler/iu,
    reason: "Datenschutz oder Minderjährige",
  },
  {
    re: /architektur|schnittstelle|(?<![\p{L}-])api(?!\p{L})|parallel|nebenläufig|performance|echtzeit/iu,
    reason: "Architektur, Schnittstellen oder Leistung",
  },
  { re: /barrierefrei|screenreader|a11y|tastatur/iu, reason: "Barrierefreiheit" },
];

/**
 * Hinweise auf reine Darstellung (Stil, Abstände, Layout). Ohne heiklen Bereich reicht dafür Sonnet, und viele
 * Kriterien bedeuten hier viele kleine Prüfpunkte, keine schwere Denkarbeit.
 */
const DISPLAY_SIGNAL =
  /(?<![\p{L}-])css|styling|(?<![\p{L}-])stil|abstand|abstände|padding|margin|layout|(?<![\p{L}-])farbe|schrift|darstellung|optik|aussehen|umbruch/iu;

/** Heikle Bereiche, die ein Text berührt (Beraterin und Autopilot). */
export function riskReasons(text: string): string[] {
  return RISK_SIGNALS.filter((r) => r.re.test(text)).map((r) => r.reason);
}

function step(effort: EffortLevel, by: number, min: EffortLevel = "low"): EffortLevel {
  const i = EFFORT_LEVELS.indexOf(effort);
  const lo = EFFORT_LEVELS.indexOf(min);
  return EFFORT_LEVELS[Math.max(lo, Math.min(EFFORT_LEVELS.length - 1, i + by))] as EffortLevel;
}

/**
 * Höchster Aufwand, den die Beraterin selbst einstellt. Sehr hohen und maximalen Aufwand sowie Fable 5.1
 * schaltet nur der Owner im Plan ein; die Beraterin empfiehlt sie höchstens (23.10.2026: automatisch gewählt
 * kosteten Fable und „max“ in einer Woche 45 % des Verbrauchs).
 */
export const AUTO_EFFORT_CAP: EffortLevel = "high";

/** Ab so vielen Kriterien gilt eine Aufgabe als umfangreich, bis zu so vielen als klein. */
const LARGE_ITEM = 5;
const SMALL_ITEM = 2;

/** Grundeinstellung je Spur und kleinster Aufwand, auf den eine kleine Aufgabe sinken darf. */
const LANE_BASE: Record<
  Lane,
  { model: ClaudeModel; effort: EffortLevel; floor: EffortLevel; reason: string }
> = {
  code: {
    model: "opus",
    effort: "high",
    floor: "medium",
    reason: "Programmieren: Opus 5.5 mit hohem Aufwand",
  },
  test: {
    model: "sonnet",
    effort: "high",
    floor: "medium",
    reason: "Tests: Sonnet 5.5 schreibt und prüft Tests zuverlässig zum halben Preis von Opus",
  },
  research: {
    model: "sonnet",
    effort: "high",
    floor: "medium",
    reason: "Recherche: Sonnet 5.5 liest und fasst sparsam zusammen",
  },
  design: {
    model: "opus",
    effort: "high",
    floor: "medium",
    reason: "Gestaltung: braucht Urteilsvermögen, selten die höchste Stufe",
  },
  docs: {
    model: "sonnet",
    effort: "medium",
    floor: "low",
    reason: "Doku: Sonnet 5.5 schreibt gut und spart rund die Hälfte gegenüber Opus",
  },
};

/**
 * Vorschlag für eine Aufgabe. Bewertet wird die Aufgabe selbst (Titel und Kriterien), nicht der ganze Auftrag:
 * Sonst machte ein Auftrag zum Thema Anmeldung jede seiner Aufgaben heikel und groß.
 */
export function adviseRun(input: AdviceInput): Advice {
  const text = [input.title, ...input.acceptanceCriteria].join(" \n");
  const risks = riskReasons(text);
  const scope = input.acceptanceCriteria.length;
  const base = LANE_BASE[input.lane];
  let model = base.model;
  let effort = base.effort;
  const reasons = [base.reason];
  const displayOnly = input.lane === "code" && risks.length === 0 && DISPLAY_SIGNAL.test(text);
  if (displayOnly) {
    model = "sonnet";
    reasons.push("Reine Darstellung (Stil, Abstände, Layout): Sonnet 5.5 reicht und spart rund die Hälfte");
  }

  if (risks.length > 0) {
    reasons.push(`Heikel: ${risks.join(", ")}`);
    if (input.lane === "test" && model === "sonnet") {
      model = "opus";
      reasons.push("Tests für heikle Bereiche: Opus statt Sonnet");
    } else {
      effort = step(effort, 1);
    }
  }
  if (input.lane === "code" && (risks.length >= 2 || (scope >= LARGE_ITEM && !displayOnly))) {
    effort = "xhigh";
    reasons.push(
      risks.length >= 2
        ? "Mehrere heikle Bereiche: sehr hoher Aufwand wäre angebracht"
        : `Umfangreich (${scope} Kriterien): sehr hoher Aufwand wäre angebracht`,
    );
  }
  if (risks.length === 0 && scope <= SMALL_ITEM) {
    const lower = step(effort, -1, base.floor);
    if (lower !== effort) {
      effort = lower;
      reasons.push("Kleine, klare Aufgabe: eine Stufe sparsamer");
    }
  }
  let recommendedEffort: EffortLevel | null = null;
  if (EFFORT_LEVELS.indexOf(effort) > EFFORT_LEVELS.indexOf(AUTO_EFFORT_CAP)) {
    recommendedEffort = effort;
    effort = AUTO_EFFORT_CAP;
    reasons.push(
      `Empfehlung: Aufwand ${recommendedEffort}; eingestellt ist ${effort}, einschalten nur durch dich`,
    );
  }
  return { model, effort, reasons, recommendedEffort };
}

/** Was eine KI (Planerin oder Prüferin) für eine Aufgabe vorschlägt; ungeprüfte Werte. */
export interface ProposedChoice {
  model?: string | null;
  effort?: string | null;
  reason?: string | null;
}

const LEVEL = (e: EffortLevel) => EFFORT_LEVELS.indexOf(e);
const isModel = (v: unknown): v is ClaudeModel =>
  typeof v === "string" && (Object.keys(MODEL_INFO) as string[]).includes(v);
const isEffort = (v: unknown): v is EffortLevel =>
  typeof v === "string" && (EFFORT_LEVELS as readonly string[]).includes(v);

/**
 * Leitplanken für den Vorschlag einer KI (24.10.2026: die KI urteilt, die Regeln sichern nur ab).
 * Fable und Aufwand über `high` stellt nie die Automatik ein, sie bleiben Empfehlung für den Owner. In heiklen
 * Bereichen arbeiten Code und Tests mindestens mit Opus und hohem Aufwand. Ohne brauchbaren Vorschlag gilt die
 * Regelberatung (`adviseRun`).
 */
export function guardChoice(
  input: AdviceInput,
  proposed: ProposedChoice,
): Advice & { source: "ki" | "regeln" } {
  const rules = adviseRun(input);
  if (!isModel(proposed.model) || !isEffort(proposed.effort)) return { ...rules, source: "regeln" };
  let model: ClaudeModel = proposed.model;
  let effort: EffortLevel = proposed.effort;
  const reasons = [proposed.reason?.trim() || `KI: ${MODEL_INFO[model].label}, Aufwand ${effort}`];
  let recommendedEffort = rules.recommendedEffort;
  if (model === "fable") {
    model = "opus";
    reasons.push("Leitplanke: Fable 5.1 schaltest nur du ein, eingestellt ist Opus 5.5");
  }
  if (LEVEL(effort) > LEVEL(AUTO_EFFORT_CAP)) {
    if (!recommendedEffort || LEVEL(effort) > LEVEL(recommendedEffort)) recommendedEffort = effort;
    effort = AUTO_EFFORT_CAP;
  }
  const risks = riskReasons([input.title, ...input.acceptanceCriteria].join(" \n"));
  if (risks.length > 0 && (input.lane === "code" || input.lane === "test")) {
    if (model === "sonnet" || LEVEL(effort) < LEVEL("high")) {
      model = "opus";
      effort = "high";
      reasons.push(`Leitplanke: heikel (${risks.join(", ")}), daher mindestens Opus 5.5 mit hohem Aufwand`);
    }
  }
  if (recommendedEffort && LEVEL(recommendedEffort) <= LEVEL(effort)) recommendedEffort = null;
  if (recommendedEffort) {
    reasons.push(
      `Empfehlung: Aufwand ${recommendedEffort}; eingestellt ist ${effort}, einschalten nur durch dich`,
    );
  }
  return { model, effort, reasons, recommendedEffort, source: "ki" };
}
